import { Link, NavLink, Outlet, useParams, useLocation } from 'react-router-dom';
import { useEventBus } from '@bus/useEventBus';
import { useEffect } from 'react';

export function AppShell() {
  const emit = useEventBus();
  // Read the current subject from the URL so quick-links stay in the right subject.
  const { subject } = useParams<{ subject?: string }>();
  const { pathname, hash } = useLocation();

  useEffect(() => {
    emit({ kind: 'session.start', payload: {} });
    return () => emit({ kind: 'session.end', payload: {} });
  }, [emit]);

  // Land at the TOP of every new page. SPA navigation doesn't reset scroll, so
  // moving between pages otherwise keeps the previous scroll position (you'd
  // land mid/bottom). Skip when a deep-link #hash is present; force instant so
  // it doesn't animate against the global scroll-behavior: smooth.
  useEffect(() => {
    if (window.location.hash) {
      // Client-side navigation does not scroll to #anchors on its own; do it after render.
      const target = decodeURIComponent(window.location.hash.slice(1));
      requestAnimationFrame(() => document.getElementById(target)?.scrollIntoView());
      return;
    }
    const root = document.documentElement;
    const prev = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    root.style.scrollBehavior = prev;
  }, [pathname, hash]);

  const sectionLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-2.5 py-1 rounded-lg text-xs font-medium transition-colors duration-150 ${
      isActive ? 'text-accent-700 bg-accent-50' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
    }`;

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-ink-200/70 bg-cream/80 backdrop-blur-xl backdrop-saturate-150 sticky top-0 z-20 shadow-nav">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="shrink-0 group inline-flex items-center gap-1.5" aria-label="ACES — home">
            <span className="font-serif text-[1.3rem] font-semibold text-ink-900 tracking-tight group-hover:text-accent-600 transition-colors duration-150">
              ACES
            </span>
            <span aria-hidden="true" className="hidden sm:inline-block h-1.5 w-1.5 rounded-full bg-accent-500 shadow-[0_0_0_3px_rgba(224,123,26,0.18)] transition-transform duration-200 group-hover:scale-125 motion-reduce:transition-none" />
          </Link>

          {/* Right cluster: section links + board quick-links */}
          <div className="flex items-center gap-2 sm:gap-3">
            <nav className="hidden sm:flex items-center gap-1 text-sm" aria-label="Site sections">
              <NavLink to="/exam-corner/" className={sectionLinkClass}>Exam Corner</NavLink>
              <NavLink to="/excel-course/" className={sectionLinkClass}>Excel Course</NavLink>
              <NavLink to="/careers/" className={sectionLinkClass}>Careers</NavLink>
              <NavLink to="/blog/" className={sectionLinkClass}>Blog</NavLink>
              <NavLink to="/ai-and-you/" className={sectionLinkClass}>AI &amp; You</NavLink>
            </nav>
            <span className="hidden sm:block w-px h-4 bg-ink-200" aria-hidden="true" />
            <nav className="flex items-center gap-1 text-sm" aria-label="Quick board navigation">
              <span className="hidden md:block text-ink-500 text-xs mr-2 uppercase tracking-wide">Go to</span>
              {[
                { board: 'cbse', grade: '11', label: 'CBSE 11' },
                { board: 'cbse', grade: '12', label: 'CBSE 12' },
                { board: 'kerala', grade: '11', label: 'Kerala +1' },
                { board: 'kerala', grade: '12', label: 'Kerala +2' },
              ].map(({ board, grade, label }) =>
                subject ? (
                  <NavLink
                    key={label}
                    to={`/${board}/${grade}/${subject}/`}
                    className={({ isActive }) =>
                      `px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150
                       ${isActive
                         ? 'bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200'
                         : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900 active:bg-ink-200'}`
                    }
                  >
                    {label}
                  </NavLink>
                ) : (
                  // Not on a subject page: open the subject chooser with this board and class already picked.
                  <Link
                    key={label}
                    to="/#navigator"
                    state={{ board, grade }}
                    className="px-2.5 py-1 rounded-md text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900 active:bg-ink-200 transition-colors duration-150"
                  >
                    {label}
                  </Link>
                ),
              )}
            </nav>
          </div>
        </div>
      </header>
      <main className="flex-1 aces-page-enter">
        <Outlet />
      </main>
      <footer className="border-t border-ink-200/80 bg-gradient-to-b from-cream to-ink-50">
        <div className="max-w-6xl mx-auto px-4 py-12 sm:py-14">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 text-xs text-ink-500">
            <div className="max-w-md">
              <div className="font-serif text-lg font-semibold text-ink-900 tracking-tight mb-2">ACES</div>
              <p className="leading-relaxed">
                Independent learning platform aligned to CBSE NCERT and Kerala SCERT Higher
                Secondary commerce syllabi.{' '}
                <strong className="text-ink-600">Not affiliated with, endorsed by, or sponsored by NCERT, CBSE, or
                the Government of Kerala.</strong>{' '}
                All trademarks are property of their respective owners.
              </p>
            </div>
            <nav className="flex flex-wrap gap-x-5 gap-y-2 md:text-right">
              {[
                { to: '/exam-corner/', label: 'Exam Corner' },
                { to: '/excel-course/', label: 'Free Excel Course' },
                { to: '/careers/',    label: 'Careers' },
                { to: '/blog/',       label: 'Blog' },
                { to: '/ai-and-you/', label: 'AI & You' },
                { to: '/about/',      label: 'About' },
                { to: '/sources/',    label: 'Sources' },
                { to: '/privacy/',    label: 'Privacy' },
                { to: '/terms/',      label: 'Terms' },
                { to: '/contact/',    label: 'Contact' },
              ].map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="text-ink-600 hover:text-ink-900 underline-offset-4 hover:underline transition-colors duration-150"
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="mt-8 pt-6 border-t border-ink-200/80 text-xs text-ink-500 flex justify-between flex-wrap gap-3">
            <span>© {new Date().getFullYear()} ACES · Built for Plus One &amp; Plus Two learners</span>
            <a
              href="https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-ink-500 hover:text-red-600 transition-colors duration-150"
              aria-label="Koshy Thomas Online Classes on YouTube"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-3.5 h-3.5 shrink-0"
                aria-hidden="true"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span>Koshy Thomas Online Classes</span>
            </a>
            <span>Made in Kerala 🇮🇳</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
